import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { adminUpdate } from '../../lib/adminApi';
import { Mail, CreditCard as Edit2, Send } from 'lucide-react';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { TemplateEditorModal } from '../../components/admin/TemplateEditorModal';

interface EmailTemplate {
  id: string;
  template_type: string;
  subject: string;
  body: string;
  html_body: string | null;
  is_user_template: boolean;
  updated_at: string;
}

export function AdminTemplates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [sendingTest, setSendingTest] = useState<string | null>(null);
  const [testMessage, setTestMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.error('No session found');
        setLoading(false);
        return;
      }

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-get-data?type=email-templates`;
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to load templates:', errorText);
        setLoading(false);
        return;
      }

      const result = await response.json();
      const allTemplates = result.data || [];
      const userTemplates = allTemplates.filter((t: EmailTemplate) => t.is_user_template);
      setTemplates(userTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
    }

    setLoading(false);
  };

  const handleEditTemplate = (template: EmailTemplate) => {
    setEditingTemplate(template);
  };

  const handleSaveTemplate = async (templateId: string, subject: string, htmlBody: string) => {
    await adminUpdate({
      table: 'email_templates',
      operation: 'update',
      id: templateId,
      data: {
        subject,
        html_body: htmlBody,
        updated_at: new Date().toISOString(),
      },
    });
    loadTemplates();
  };

  const handleSendTestEmail = async (template: EmailTemplate) => {
    setSendingTest(template.id);
    setTestMessage(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Geen sessie gevonden');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            to: 'admin@dibbi.nl',
            template_type: template.template_type,
            variables: {
              first_name: 'Test Gebruiker',
              company_name: 'Test Bedrijf BV',
              magic_link: `${window.location.origin}/auth/verify?token=test-token-12345`,
              blog_title: 'Test Blog Titel',
              campaign_subject: 'Test Campaign',
              total_contacts: '100',
              sent_count: '95',
              failed_count: '5',
              success_rate: '95',
              sent_at: new Date().toLocaleString('nl-NL'),
              campaign_url: `${window.location.origin}/admin/campaigns`,
              platform: 'LinkedIn',
              post_preview: 'Dit is een voorbeeldtekst van een social media post die automatisch is aangemaakt door Dibbi op basis van je laatste blogpost.',
            }
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Email verzenden mislukt');
      }

      setTestMessage({
        type: 'success',
        text: `Test email succesvol verzonden naar admin@dibbi.nl`
      });

      setTimeout(() => {
        setTestMessage(null);
      }, 5000);
    } catch (error: any) {
      setTestMessage({
        type: 'error',
        text: `Fout bij verzenden: ${error.message}`
      });
    } finally {
      setSendingTest(null);
    }
  };

  const getTemplateDisplayName = (templateType: string): string => {
    const names: Record<string, string> = {
      magic_link: 'Magic Link Login',
      blog_reminder: 'Blog Reminder',
      campaign_completion_notification: 'Campaign Completion',
      social_media_post_sent: 'Social Media Post Verzonden',
      whatsapp_ideas_ready: 'WhatsApp: ideeën klaar',
    };
    return names[templateType] || templateType;
  };

  const getTemplateDescription = (templateType: string): string => {
    const descriptions: Record<string, string> = {
      magic_link: 'Email met inloglink voor gebruikers',
      blog_reminder: 'Herinnering om nieuwe blog te schrijven',
      campaign_completion_notification: 'Notificatie na succesvol verzenden campaign',
      social_media_post_sent: 'Bevestiging na succesvolle social media post via webhook',
      whatsapp_ideas_ready: 'Mail bij nieuwe ideeën via WhatsApp. Variabelen: {{first_name}}, {{magic_link}}.',
    };
    return descriptions[templateType] || '';
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <LoadingSpinner text="Templates laden..." />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Email Templates</h1>
          <p className="text-gray-600">Bewerk de gebruiker-gerichte email templates</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((template) => (
          <div key={template.id} className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
            <div className="p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <Mail className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">
                    {getTemplateDisplayName(template.template_type)}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {getTemplateDescription(template.template_type)}
                  </p>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-1">Onderwerp:</p>
                <p className="text-sm text-gray-600 line-clamp-2">{template.subject}</p>
              </div>

              <div className="text-xs text-gray-500 mb-4">
                Laatst gewijzigd: {new Date(template.updated_at).toLocaleDateString('nl-NL')}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => handleEditTemplate(template)}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Edit2 className="w-4 h-4" />
                  Bewerken
                </button>
                <button
                  onClick={() => handleSendTestEmail(template)}
                  disabled={sendingTest === template.id}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                  title="Verstuur test email"
                >
                  <Send className={`w-4 h-4 ${sendingTest === template.id ? 'animate-pulse' : ''}`} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {testMessage && (
        <div className={`fixed bottom-8 right-8 bg-white rounded-lg shadow-lg border p-4 max-w-md z-50 ${
          testMessage.type === 'success'
            ? 'bg-green-50 border-green-200'
            : 'bg-red-50 border-red-200'
        }`}>
          <p className={`text-sm ${
            testMessage.type === 'success' ? 'text-green-600' : 'text-red-600'
          }`}>
            {testMessage.text}
          </p>
        </div>
      )}

      {editingTemplate && (
        <TemplateEditorModal
          template={editingTemplate}
          onClose={() => setEditingTemplate(null)}
          onSave={handleSaveTemplate}
        />
      )}
    </AdminLayout>
  );
}
